'use client';

import { useSyncExternalStore } from 'react';
import type { Sport } from '@sharpr/types';
import { useQuery } from '@tanstack/react-query';
import { accessTier, type AccessTier, type SportsAccess } from '@/lib/access';
import { sessionQuery } from './useSession';
import { useAccessState } from './useAccessState';

export interface Access {
  tier: (sport: Sport) => AccessTier;
  // The cut the viewer asks for: the tier, except an admin previewing the subscriber view (the admin console's toggle).
  view: (sport: Sport) => AccessTier;
  isAdmin: boolean;
  loading: boolean;   // the session hasn't loaded (no claim yet either)
}

const NO_ACCESS: SportsAccess = { isAdmin: false, sportsAccess: [] };
const selectTokenAccess = (s: { access: SportsAccess }) => s.access;

// The viewer's access, for routing and display only: the server cuts every response by its own live check. It is the
// live status once loaded (kept through a failed refresh), else the access-token claim, so first paint needs no network.
export function useAccess(): Access {
  const token = useQuery({ ...sessionQuery, select: selectTokenAccess });
  const { accessState, resolved } = useAccessState();
  const adminView = useAdminView();
  const { isAdmin, sportsAccess } = resolved ? accessState : token.data ?? NO_ACCESS;
  const tier = (sport: Sport) => accessTier({ isAdmin, hasSport: sportsAccess.includes(sport) });
  return {
    tier,
    view: sport => { const t = tier(sport); return t === 'admin' && !adminView ? 'subscriber' : t; },
    isAdmin,
    loading: token.isLoading,
  };
}

// An admin's choice between the admin view and a preview of what subscribers see: a per-browser preference (this tab
// and every other one follow it). Read at render, so the first render already asks for the right cut.
const ADMIN_VIEW_KEY = 'admin_view_enabled';
const listeners = new Set<() => void>();
let unstored = false;   // the choice where storage is unavailable: this page only

function readAdminView(): boolean {
  try {
    return localStorage.getItem(ADMIN_VIEW_KEY) === 'true';
  } catch {
    return unstored;
  }
}

function subscribeAdminView(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => { if (e.key === ADMIN_VIEW_KEY) onChange(); };
  listeners.add(onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function useAdminView(): boolean {
  return useSyncExternalStore(subscribeAdminView, readAdminView, () => false);
}

export function setAdminView(on: boolean): void {
  unstored = on;
  try {
    localStorage.setItem(ADMIN_VIEW_KEY, String(on));
  } catch {
    // Storage unavailable: readAdminView falls back to `unstored`.
  }
  for (const l of listeners) l();
}
